import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";

import BrochureWizard from "../../../../components/Modals/GenerateReport/BrochureWizard";
import Header from "../../../../components/ui/ListHeader";
import { Modal } from "../../../../components/ui";
import {
  DELETE_REPORT_JOB,
  RETRY_REPORT_JOB,
  SAVE_REPORT_DRAFT,
} from "../../../../graphql/mutation/ReportJob";
import { FETCH_REPORT_JOBS } from "../../../../graphql/query/ReportJob";
import PdfGeneratorInstructions from "../../components/PdfGeneratorInstructions";
import ReportJobSummary from "../../components/ReportJobSummary";
import {
  buildDraftInput,
  wizardStateFromDraftPayload,
} from "../../../../components/Modals/GenerateReport/wizardUtils";
import {
  formatReportJobAttempts,
  formatReportJobDate,
  formatReportJobProgress,
  formatReportJobRange,
  formatReportJobTimestamps,
  hasActiveReportJobs,
  highlightedJobIdFromSearch,
  isReissuableReportJob,
  isReportJobActive,
  isReportJobDeletable,
  isResumableDraft,
  isRetryableReportJob,
  isStuckReportJob,
  REPORT_JOB_PAGE_SIZE,
  REPORT_JOB_STATUS_LABELS,
  REPORT_JOB_STATUS_STYLES,
  REPORT_SECTIONS,
  reportJobLabel,
  reportJobPageCount,
  reportJobPageFromSearch,
  reportJobPageSearch,
  reportJobScope,
  reportJobSectionFilter,
  reportJobSourceType,
  reportSectionMeta,
} from "../../utils";

const POLL_INTERVAL_MS = 4000;

const StatusBadge = ({ job }) => (
  <span
    className={`inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold ${
      isStuckReportJob(job)
        ? "bg-amber-100 text-amber-800"
        : REPORT_JOB_STATUS_STYLES[job?.status] || "bg-gray-100 text-gray-700"
    }`}
  >
    {isStuckReportJob(job)
      ? "Stuck"
      : REPORT_JOB_STATUS_LABELS[job?.status] || job?.status}
    {isReportJobActive(job) && formatReportJobProgress(job) ? (
      <span className="ml-1 font-normal">({formatReportJobProgress(job)})</span>
    ) : null}
  </span>
);

const ReportJobRow = ({
  job,
  showFormat,
  highlighted,
  rowRef,
  onResumeDraft,
  onReissue,
  reissuing,
  onRetry,
  retrying,
  onDelete,
  deleting,
}) => (
  <tr
    ref={rowRef}
    className={`border-b border-gray-100 text-sm ${
      highlighted ? "bg-blue-50 ring-1 ring-inset ring-blue-200" : ""
    }`}
  >
    <td className="py-3 pr-4 align-top">
      <ReportJobSummary job={job} />
    </td>
    <td className="px-4 py-3 align-top text-gray-500">
      {reportJobSourceType(job)}
    </td>
    <td className="whitespace-nowrap px-4 py-3 align-top text-gray-500">
      {reportJobScope(job)}
    </td>
    {showFormat ? (
      <td className="px-4 py-3 align-top uppercase text-gray-500">
        {job?.format}
      </td>
    ) : null}
    <td className="px-4 py-3 align-top">
      <StatusBadge job={job} />
      {job?.error && (job?.status === "failed" || isStuckReportJob(job)) ? (
        <div className="mt-1 max-w-md break-words text-xs text-red-600">
          {job.error}
        </div>
      ) : null}
      {formatReportJobAttempts(job) ? (
        <div className="mt-1 text-xs text-gray-400">
          {formatReportJobAttempts(job)}
        </div>
      ) : null}
    </td>
    <td
      className="whitespace-nowrap px-4 py-3 align-top text-gray-500"
      title={formatReportJobTimestamps(job)}
    >
      {formatReportJobDate(job)}
    </td>
    <td className="py-3 pl-4 text-right align-top">
      {/* Wraps rather than squeezing the rest of the table: a complete brochure
          carries Reissue, Retry, Download and Delete at once, which needs more
          width than a narrow window has. */}
      <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1">
        {isResumableDraft(job) ? (
          <button
            type="button"
            className="font-medium text-blue-600 hover:text-blue-800"
            onClick={() => onResumeDraft(job)}
          >
            Resume
          </button>
        ) : null}
        {isReissuableReportJob(job) ? (
          <button
            type="button"
            className="font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50"
            disabled={reissuing}
            onClick={() => onReissue(job)}
          >
            {reissuing ? "Reissuing…" : "Reissue"}
          </button>
        ) : null}
        {isRetryableReportJob(job) ? (
          <button
            type="button"
            className="font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50"
            disabled={retrying}
            onClick={() => onRetry(job)}
          >
            {retrying ? "Retrying…" : "Retry"}
          </button>
        ) : null}
        {job?.status === "complete" && job?.downloadUrl ? (
          <a
            className="font-medium text-blue-600 hover:text-blue-800"
            href={job.downloadUrl}
            download={job?.filename || true}
          >
            Download
          </a>
        ) : null}
        {isReportJobDeletable(job) ? (
          <button
            type="button"
            className="font-medium text-red-600 hover:text-red-800 disabled:opacity-50"
            disabled={deleting}
            onClick={() => onDelete(job)}
          >
            Delete
          </button>
        ) : null}
      </div>
    </td>
  </tr>
);

const List = ({ section = REPORT_SECTIONS.REPORTING }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.toString();
  const highlightedJobId = highlightedJobIdFromSearch(search);
  const highlightedRowRef = useRef(null);
  const sectionMeta = reportSectionMeta(section);
  const showFormat = Boolean(sectionMeta.showsFormatColumn);

  // The page lives in the url, so reloading or coming back to a report keeps
  // your place. The two sections are separate routes, so each starts at its own
  // first page rather than inheriting the other's.
  const page = reportJobPageFromSearch(search);
  const goToPage = useCallback(
    (next, { replace = false } = {}) =>
      setSearchParams(reportJobPageSearch(search, next), { replace }),
    [search, setSearchParams],
  );

  const { data, loading, error, refetch, startPolling, stopPolling } = useQuery(
    FETCH_REPORT_JOBS,
    {
      variables: {
        input: {
          ...reportJobSectionFilter(section),
          limit: REPORT_JOB_PAGE_SIZE,
          offset: page * REPORT_JOB_PAGE_SIZE,
        },
      },
      fetchPolicy: "cache-and-network",
    },
  );

  const [deleteReportJob] = useMutation(DELETE_REPORT_JOB);
  const [deletingJobId, setDeletingJobId] = useState(null);
  const [deleteError, setDeleteError] = useState(null);

  const [saveReportDraft] = useMutation(SAVE_REPORT_DRAFT);
  const [reissuingJobId, setReissuingJobId] = useState(null);
  const [reissueError, setReissueError] = useState(null);

  const [retryReportJob] = useMutation(RETRY_REPORT_JOB);
  const [retryingJobId, setRetryingJobId] = useState(null);
  const [retryError, setRetryError] = useState(null);

  // The draft being resumed in the wizard modal (null when closed). Keyed
  // into the Modal so reopening a different draft remounts the wizard.
  const [resumingDraft, setResumingDraft] = useState(null);

  // Already narrowed to this section, and to this user, by the query.
  const jobs = data?.reportJobs?.edges || [];
  const loadedCount = data?.reportJobs?.count ?? null;
  const count = loadedCount ?? 0;
  const pageCount = reportJobPageCount(count);

  // Deleting the last report on the last page would otherwise leave the pager
  // pointing past the end, at an empty table. Only once a count is in hand:
  // there is none while the page being asked for is still on its way, and
  // clamping against that would send the pager straight back to the first page.
  useEffect(() => {
    if (loadedCount != null && page > pageCount - 1) {
      goToPage(pageCount - 1, { replace: true });
    }
  }, [goToPage, loadedCount, page, pageCount]);

  // Scroll the highlighted row (arrived at via the header bell) into view
  // once it has rendered.
  const highlightedLoaded = jobs.some(job => job.id === highlightedJobId);
  useEffect(() => {
    if (highlightedLoaded && highlightedRowRef.current) {
      highlightedRowRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [highlightedLoaded, highlightedJobId]);

  // Only keep polling while something is actually in flight, so an idle
  // history page doesn't hammer the api.
  const active = hasActiveReportJobs(jobs);
  useEffect(() => {
    if (active) {
      startPolling(POLL_INTERVAL_MS);
      return () => stopPolling();
    }
    stopPolling();
    return undefined;
  }, [active, startPolling, stopPolling]);

  // Reissue: copy the finished job's saved configuration into a brand new
  // draft (the original job is left untouched) and open the wizard on it with
  // the order, cover and per-instance selections preselected.
  const handleReissue = async job => {
    setReissuingJobId(job.id);
    setReissueError(null);
    try {
      const { data: saved } = await saveReportDraft({
        variables: {
          input: buildDraftInput(wizardStateFromDraftPayload(job.payload)),
        },
      });
      const result = saved?.saveReportDraft;
      if (!result?.success || !result?.reportJob) {
        const message = Array.isArray(result?.error)
          ? result.error.join(", ")
          : result?.error || "Failed to reissue the report";
        throw new Error(message);
      }
      setResumingDraft(result.reportJob);
      await refetch();
    } catch (err) {
      setReissueError(err.message || "Failed to reissue the report");
    } finally {
      setReissuingJobId(null);
    }
  };

  // Retry: put the same job back on the queue. Unlike Reissue this keeps the
  // row (and its id), so the history doesn't grow an entry every time the
  // worker has a bad day.
  const handleRetry = async job => {
    setRetryingJobId(job.id);
    setRetryError(null);
    try {
      const { data: retried } = await retryReportJob({
        variables: { id: job.id },
      });
      const result = retried?.retryReportJob;
      if (!result?.success) {
        const message = Array.isArray(result?.error)
          ? result.error.join(", ")
          : result?.error || "Failed to retry the report";
        throw new Error(message);
      }
      await refetch();
    } catch (err) {
      setRetryError(err.message || "Failed to retry the report");
    } finally {
      setRetryingJobId(null);
    }
  };

  const handleDelete = async job => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete this ${reportJobLabel(job)} report?`)) return;
    setDeletingJobId(job.id);
    setDeleteError(null);
    try {
      const { data: deleted } = await deleteReportJob({
        variables: { id: job.id },
      });
      if (!deleted?.deleteReportJob?.success) {
        throw new Error(
          deleted?.deleteReportJob?.error || "Failed to delete the report",
        );
      }
      await refetch();
    } catch (err) {
      setDeleteError(err.message || "Failed to delete the report");
    } finally {
      setDeletingJobId(null);
    }
  };

  const inFlight = jobs.filter(isReportJobActive);

  return (
    <>
      <Header
        leftText={sectionMeta.label}
        rightComponent={() =>
          inFlight.length > 0 ? (
            <div className="text-sm text-gray-500">
              {inFlight.length} report{inFlight.length === 1 ? "" : "s"} in
              progress
            </div>
          ) : null
        }
      />
      <div className="mt-6 w-full px-4 sm:px-6 lg:px-8">
        {section === REPORT_SECTIONS.PDF_GENERATOR ? (
          <PdfGeneratorInstructions />
        ) : null}
        {error ? (
          <div className="text-sm text-red-600">
            Failed to load report jobs: {error.message}
          </div>
        ) : null}
        {deleteError ? (
          <div className="text-sm text-red-600">{deleteError}</div>
        ) : null}
        {reissueError ? (
          <div className="text-sm text-red-600">{reissueError}</div>
        ) : null}
        {retryError ? (
          <div className="text-sm text-red-600">{retryError}</div>
        ) : null}
        {loading && jobs.length === 0 ? (
          <div className="text-sm text-gray-500">Loading…</div>
        ) : null}
        {!loading && jobs.length === 0 && !error ? (
          <div className="text-sm text-gray-500">
            {sectionMeta.emptyMessage}
          </div>
        ) : null}
        {jobs.length > 0 ? (
          // The columns wrap and shrink a long way before this bites, but a very
          // narrow window still gets a scrollable table rather than a squashed
          // one that pushes the page sideways.
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] border-collapse text-left">
              <thead>
                <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                  {/* The outer edges sit flush with the page gutter, so the first
                    and last columns drop their outward padding. */}
                  <th className="py-2 pr-4">Report</th>
                  <th className="px-4 py-2">Type</th>
                  <th className="px-4 py-2">Scope</th>
                  {showFormat ? <th className="px-4 py-2">Format</th> : null}
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Date</th>
                  <th className="py-2 pl-4" />
                </tr>
              </thead>
              <tbody>
                {jobs.map(job => (
                  <ReportJobRow
                    key={job.id}
                    job={job}
                    showFormat={showFormat}
                    highlighted={job.id === highlightedJobId}
                    rowRef={
                      job.id === highlightedJobId
                        ? highlightedRowRef
                        : undefined
                    }
                    onResumeDraft={setResumingDraft}
                    onReissue={handleReissue}
                    reissuing={reissuingJobId === job.id}
                    onRetry={handleRetry}
                    retrying={retryingJobId === job.id}
                    onDelete={handleDelete}
                    deleting={deletingJobId === job.id}
                  />
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        {jobs.length > 0 ? (
          <div className="mb-8 mt-4 flex flex-wrap items-center justify-between gap-2 text-sm">
            <div className="text-gray-500">
              {formatReportJobRange({
                count,
                offset: page * REPORT_JOB_PAGE_SIZE,
                shown: jobs.length,
              })}
            </div>
            {pageCount > 1 ? (
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  className="font-medium text-blue-600 hover:text-blue-800 disabled:text-gray-300"
                  disabled={page === 0}
                  onClick={() => goToPage(Math.max(page - 1, 0))}
                >
                  Previous
                </button>
                <span className="text-gray-500">
                  Page {page + 1} of {pageCount}
                </span>
                <button
                  type="button"
                  className="font-medium text-blue-600 hover:text-blue-800 disabled:text-gray-300"
                  disabled={page >= pageCount - 1}
                  onClick={() => goToPage(Math.min(page + 1, pageCount - 1))}
                >
                  Next
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
      {resumingDraft ? (
        <Modal
          key={resumingDraft.id}
          isOpen
          scale="lg"
          hideCloseButton
          closeOnBackdrop={false}
          body={BrochureWizard}
          draftJob={resumingDraft}
          onClose={() => {
            setResumingDraft(null);
            refetch();
          }}
        />
      ) : null}
    </>
  );
};

export default List;
